/**
 * Date string formatting utilities
 */

export type DateStringFormat = 'YYYY-MM-DD' | 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DDTHH:mm:ss' | 'ISO';

/**
 * Formats a Date object to a string in the specified format
 * @param date - Date object to format
 * @param format - Desired output format
 * @returns Formatted date string
 */
export function formatDateString(date: Date, format: DateStringFormat = 'YYYY-MM-DD'): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  switch (format) {
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'YYYY-MM-DDTHH:mm:ss':
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    case 'ISO':
      return date.toISOString();
    default:
      return `${year}-${month}-${day}`;
  }
}

/**
 * Formats a date string from one format to another
 * @param dateString - Input date string
 * @param fromFormat - Current format of the input string
 * @param toFormat - Desired output format
 * @returns Formatted date string
 */
export function convertDateString(
  dateString: string, 
  fromFormat: DateStringFormat, 
  toFormat: DateStringFormat
): string {
  const date = parseDateString(dateString, fromFormat);
  return formatDateString(date, toFormat);
}

/**
 * Parses a date string in the specified format to a Date object
 * @param dateString - Date string to parse
 * @param format - Format of the input string
 * @returns Date object
 */
export function parseDateString(dateString: string, format: DateStringFormat): Date {
  switch (format) {
    case 'YYYY-MM-DD':
      return new Date(dateString);
    case 'MM/DD/YYYY': {
      const [month, day, year] = dateString.split('/');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    case 'DD/MM/YYYY': {
      const [dayDD, monthMM, yearYY] = dateString.split('/');
      return new Date(parseInt(yearYY), parseInt(monthMM) - 1, parseInt(dayDD));
    }
    case 'YYYY-MM-DDTHH:mm:ss':
      return new Date(dateString);
    case 'ISO':
      return new Date(dateString);
    default:
      return new Date(dateString);
  }
}

/**
 * Generates a date string for calendar date selection (YYYY-MM-DD format)
 * @param year - Year
 * @param month - Month (1-12)
 * @param day - Day (1-31)
 * @returns Formatted date string
 */
export function generateCalendarDateString(year: number, month: number, day: number): string {
  const date = new Date(year, month - 1, day);
  return formatDateString(date, 'YYYY-MM-DD');
}

/**
 * Extracts date components from a date string
 * @param dateString - Date string to parse
 * @param format - Format of the input string
 * @returns Object with year, month, and day
 */
export function extractDateComponents(dateString: string, format: DateStringFormat = 'YYYY-MM-DD'): {
  year: number;
  month: number;
  day: number;
} {
  const date = parseDateString(dateString, format);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

/**
 * Checks if a date string is valid
 * @param dateString - Date string to validate
 * @param format - Expected format
 * @returns True if the date string is valid
 */
export function isValidDateString(dateString: string, format: DateStringFormat = 'YYYY-MM-DD'): boolean {
  try {
    const date = parseDateString(dateString, format);
    return !isNaN(date.getTime());
  } catch {
    return false;
  }
}

/**
 * Gets the current date in the specified format
 * @param format - Desired output format
 * @returns Current date as formatted string
 */
export function getCurrentDateString(format: DateStringFormat = 'YYYY-MM-DD'): string {
  return formatDateString(new Date(), format);
}

/**
 * Adds days to a date string and returns the result
 * @param dateString - Input date string
 * @param days - Number of days to add (can be negative)
 * @param format - Format of the input and output strings
 * @returns New date string with days added
 */
export function addDaysToDateString(
  dateString: string, 
  days: number, 
  format: DateStringFormat = 'YYYY-MM-DD'
): string {
  const date = parseDateString(dateString, format);
  date.setDate(date.getDate() + days);
  return formatDateString(date, format);
}

/**
 * Gets the start of the month for a given date string
 * @param dateString - Input date string
 * @param format - Format of the input and output strings
 * @returns Date string for the first day of the month
 */
export function getMonthStart(dateString: string, format: DateStringFormat = 'YYYY-MM-DD'): string {
  const date = parseDateString(dateString, format);
  const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
  return formatDateString(firstDay, format);
}

/**
 * Gets the end of the month for a given date string
 * @param dateString - Input date string
 * @param format - Format of the input and output strings
 * @returns Date string for the last day of the month
 */
export function getMonthEnd(dateString: string, format: DateStringFormat = 'YYYY-MM-DD'): string {
  const date = parseDateString(dateString, format);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return formatDateString(lastDay, format);
}
